import { parseDateKey } from './dateKey';

/**
 * Two independent, owner-confirmed rules for the Flights calendar. Both
 * are best-effort reconstructions restated to the owner and corrected by
 * him directly — treat any future correction the same way, in this file,
 * without touching the calendar UI.
 *
 * 1. GREEN "free to travel" weeks (isVisitationFree): a simple alternating
 *    block, not a day-by-day rule. The owner explicitly replaced an
 *    earlier per-day version of this (which had exceptions for Tuesday
 *    nights and Friday/Saturday/Sunday) with: every other Wednesday
 *    through the following Tuesday, ALL SEVEN DAYS, starting with
 *    2026-09-09 (Wed) through 2026-09-15 (Tue) as the first green block.
 *    No day-of-week exceptions inside a green block.
 *
 * 2. "Chip has kids" Fridays (chipHasKidsLabel): unrelated UI element,
 *    unchanged by the above — every other Friday starting 2026-09-04.
 *    These two rules are NOT required to be inverses of each other; they
 *    happen to never land on the same day (chipHasKidsLabel weeks are
 *    2026-09-04, 09-18, 10-02, ...; green weeks start 2026-09-09, 09-23,
 *    10-07, ... — offset by 5 days, same 14-day period, so they never
 *    collide) but that's a coincidence of the two anchor dates, not
 *    something this code assumes or depends on.
 */

const ANCHOR_GREEN_WEDNESDAY = new Date(2026, 8, 9); // first green block: Wed 9/9 - Tue 9/15
const ANCHOR_CHIP_FRIDAY = new Date(2026, 8, 4); // first "Chip has kids" Friday
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

function mostRecentWeekday(date: Date, targetWeekday: number): Date {
  const weekday = date.getDay(); // 0=Sun..6=Sat
  const daysBack = (weekday - targetWeekday + 7) % 7;
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() - daysBack);
  return result;
}

/** True every other week, alternating from `anchor` (itself always true). */
function isAlternateWeekFrom(weekStart: Date, anchor: Date): boolean {
  const weeksSinceAnchor = Math.round((weekStart.getTime() - anchor.getTime()) / MS_PER_WEEK);
  return weeksSinceAnchor % 2 === 0;
}

/** Green calendar block: every other Wed-through-Tue week, all 7 days. */
export function isVisitationFree(dateKey: string): boolean {
  const date = parseDateKey(dateKey);
  const wednesday = mostRecentWeekday(date, 3); // 3 = Wednesday
  return isAlternateWeekFrom(wednesday, ANCHOR_GREEN_WEDNESDAY);
}

/** "Chip has kids" — every other Friday, starting 2026-09-04. Friday only. */
export function chipHasKidsLabel(dateKey: string): string | null {
  const date = parseDateKey(dateKey);
  if (date.getDay() !== 5) return null;
  return isAlternateWeekFrom(date, ANCHOR_CHIP_FRIDAY) ? 'Chip has kids' : null;
}
